import { useEffect, useRef, useState } from 'react';
import {
	createCheckoutSession,
	observeCheckoutSession,
} from '../services/stripe.service';
import { uploadPatternPdf } from '../services/storage.service';
import {
	getFileNameAndMediaTokenFromURL,
	getSinglePatternURLFromFileNameAndMediaToken,
} from '../utils/url';
import {
	trackPurchaseEvent,
	ANALYTICS_EVENTS,
} from '../services/analytics.service';

// Manages Stripe checkout session creation and redirect.
// Stores the onSnapshot unsubscribe function in a ref and cleans it up on unmount
// to prevent the memory leak that existed in Purchase.jsx (p2-snapshot-cleanup).
export const useStripeCheckout = (user, getPattern) => {
	const [inProgress, setInProgress] = useState(false);
	const unsubscribeRef = useRef(null);

	const cleanup = () => {
		if (unsubscribeRef.current) {
			unsubscribeRef.current();
			unsubscribeRef.current = null;
		}
	};

	// Unsubscribe on unmount in case the component is removed mid-checkout.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => cleanup, []);

	const startCheckout = (uid, sessionData) => {
		return new Promise((resolve, reject) => {
			cleanup();
			setInProgress(true);

			createCheckoutSession(uid, sessionData)
				.then((docRef) => {
					unsubscribeRef.current = observeCheckoutSession(
						uid,
						docRef.id,
						({ error, url } = {}) => {
							if (error) {
								cleanup();
								setInProgress(false);
								reject(new Error(error.message));
							}
							if (url) {
								cleanup();
								trackPurchaseEvent(
									ANALYTICS_EVENTS.STRIPE_REDIRECT_INITIATED,
								);
								window.location.assign(url);
								resolve();
							}
						},
					);
				})
				.catch((err) => {
					setInProgress(false);
					reject(err);
				});
		});
	};

	const purchaseSubscription = (priceId) =>
		startCheckout(user.uid, {
			price: priceId,
			success_url: window.location.origin,
			cancel_url: window.location.origin,
			allow_promotion_codes: true,
		});

	const purchaseSinglePattern = async (priceId) => {
		const { blob } = await getPattern();

		const downloadURL = await uploadPatternPdf(blob, 'singlePatterns', {
			paid: 'false',
			uid: user.uid,
		});

		const { n, mt } = getFileNameAndMediaTokenFromURL(downloadURL);
		const success_url = getSinglePatternURLFromFileNameAndMediaToken(n, mt);
		const cancel_url = getSinglePatternURLFromFileNameAndMediaToken(n);

		return startCheckout(user.uid, {
			price: priceId,
			success_url,
			cancel_url,
			allow_promotion_codes: true,
			mode: 'payment',
		});
	};

	const purchaseOneTime = (priceId) =>
		startCheckout(user.uid, {
			price: priceId,
			success_url: window.location.origin,
			cancel_url: window.location.origin,
			allow_promotion_codes: true,
			mode: 'payment',
		});

	return {
		purchaseSubscription,
		purchaseSinglePattern,
		purchaseOneTime,
		inProgress,
		cleanup,
	};
};
